import React from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo, LogoMark } from "@/components/Logo";
import {
  LayoutDashboard,
  Sparkles,
  PieChart,
  Target,
  MessagesSquare,
  Receipt,
  FileText,
  LogOut,
} from "lucide-react";

const nav = [
  { to: "/app", label: "Home", icon: LayoutDashboard, end: true, id: "home" },
  { to: "/app/invest", label: "Invest", icon: Sparkles, id: "invest" },
  { to: "/app/portfolio", label: "Portfolio", icon: PieChart, id: "portfolio" },
  { to: "/app/goals", label: "Goals", icon: Target, id: "goals" },
  { to: "/app/assistant", label: "Assistant", icon: MessagesSquare, id: "assistant" },
  { to: "/app/activity", label: "Activity", icon: Receipt, id: "activity" },
  { to: "/app/reports", label: "Reports", icon: FileText, id: "reports" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const initials = (user?.name || "U")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="min-h-screen bg-background flex flex-col md:flex-row">
      {/* Sidebar */}
      <aside className="md:w-64 md:min-h-screen border-b md:border-b-0 md:border-r border-slate-200 bg-white/80 backdrop-blur-xl md:sticky md:top-0 md:h-screen flex md:flex-col z-20">
        <div className="p-5 md:p-6 border-r md:border-r-0 md:border-b border-slate-200 flex items-center">
          <Logo size={30} tagline />
        </div>

        <nav className="flex md:flex-col gap-1 p-3 md:p-4 flex-1 overflow-x-auto scrollbar-thin">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.id}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-2xl text-sm font-medium whitespace-nowrap transition-colors duration-200 ${
                  isActive
                    ? "bg-slate-900 text-white"
                    : "text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                }`
              }
            >
              <item.icon size={19} strokeWidth={1.75} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex flex-col gap-3 p-4 border-t border-slate-200">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-xs font-bold">
              {initials}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate text-slate-900">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={async () => {
              await logout();
              navigate("/");
            }}
            className="flex items-center gap-2 text-sm text-slate-500 hover:text-red-500 transition-colors duration-200"
          >
            <LogOut size={17} strokeWidth={1.75} /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
